// The keyboard reference (plan §5.1): one table of every binding of the
// season map, grouped the way the KEYS overlay shows them. The rows are
// assembled from `KEYS.map`, which balancegen writes from
// `data/presentation/keybindings.toml` (R853), so this module states no key
// cap and no claim of its own — it pairs each binding with the sentence the
// locale carries for it, and the handlers in `fast/input.ts` (map and
// orders) and `ui/overlay.ts` (the menus, the heat maps and the control
// groups) answer the same rows. The keys of the card of a docked fleet
// (`KEYS.card`, R1989) close the table: they win over a map key while the
// card stands, so the conflict test leaves them out.
//
// Keyboard is desktop-only sugar (mobile is a target platform): every key
// below is a shortcut for an on-screen control, never the only way.

import { HEAT_MODES } from '../state';
import type { FeatureFlag } from '../gen/featureflags';
import { keymap as S } from '../gen/strings';
import {
  KEY_GROUPS, KEYS, type CardKeyId, type KeyBinding as PackBinding, type MapKeyId,
} from '../gen/presentation';
import { claimLabel } from '../keys';
import { LAYER_TOGGLES } from './layerview';
import { casinoHidden } from '../platform/platform';

export interface KeyBinding {
  /** The key label as shown ('f', 'shift-tab', 'esc'). */
  key: string;
  /** The e.key / e.code values the handlers claim, for the conflict test. */
  claims: string[];
  does: string;
  /**
   * A binding the KEYS panel does not list (R456). The doc is still the
   * spec and states it; the panel hides it, because these keys serve the
   * owner and a reviewer and not a player. The conflict test below still
   * sees the claim, so a hidden key cannot take a key a player needs.
   */
  hidden?: boolean;
  /**
   * The feature flag the row stands under (R2870): the KEYS panel lists
   * the row only while the world latched it. The heat maps CURRENTS and
   * DEPTH carry one. The conflict test still sees the claim.
   */
  flag?: FeatureFlag;
}

export interface KeyGroup {
  name: string;
  keys: KeyBinding[];
  /**
   * The keys of a card over the map (`[keys.card]`, R1989). They win over
   * the map claim of the same key while the card stands, so they may
   * repeat a map claim, and the conflict test leaves them out.
   */
  over?: boolean;
  /** The KEYS panel folds the rows under the head (R2184): the ten
   *  heat-map rows. */
  fold?: boolean;
  /** The table keys of the casino: a store app hides the group with the
   *  casino (mobile-apps.md §8, R2927). */
  casino?: boolean;
}

/** One reference row: the binding's cap and claims, plus its sentence. */
function bindingRow(b: PackBinding, does: string): KeyBinding {
  return { key: b.label, claims: [...b.claims], does, hidden: b.hidden };
}

/** One reference row of the map keys. */
function row(id: MapKeyId, does: string): KeyBinding {
  return bindingRow(KEYS.map[id], does);
}

/** The heading of each group, in the locale's words. */
const GROUP_NAMES: Record<typeof KEY_GROUPS.map[number], string> = {
  view: S.group_view,
  map: S.group_map,
  orders: S.group_orders,
  groups: S.group_groups,
  menus: S.group_menus,
  base: S.group_base,
  casino: S.group_casino,
  debug: S.group_debug,
  layers: S.group_layers,
};

/** The rows of each group, in the order the panel prints them. */
const GROUP_ROWS: Record<typeof KEY_GROUPS.map[number], KeyBinding[]> = {
  // The heat maps are ONE binding of one claim per mode, and the panel
  // expands it over the modes, so the reference cannot drift from what the keys
  // do. The mode list order is the claim order (R853).
  view: HEAT_MODES.map((m, i) => ({
    key: claimLabel(KEYS.map.heat.claims[i]),
    claims: [KEYS.map.heat.claims[i]],
    does: m.what,
    flag: m.flag,
  })),
  map: [
    // The pointer gesture is no binding, so it claims nothing and takes its
    // name from the locale.
    { key: S.does_pan_name, claims: [], does: S.does_pan },
    row('escape', S.does_escape),
    // The one pointer chord (R1294): alt with a click puts a map marker.
    row('marker', S.does_marker),
    // The held comma opens the quick wheel at the pointer (R1607).
    row('quick', S.does_quick),
    row('home', S.does_home),
    // Photo mode (R1623, ui-map-interface.md §13.1).
    row('photo', S.does_photo),
    // The depth look (R2713, ui-map-interface.md §17.3.09).
    row('depth_look', S.does_depth_look),
    row('help', S.does_help),
  ],
  orders: [
    row('fire', S.does_fire),
    row('hunt', S.does_hunt),
    row('patrol', S.does_patrol),
    row('escort', S.does_escort),
    row('defend', S.does_defend),
    row('return', S.does_return),
    row('anchor', S.does_anchor),
    // The round switch shares `n` with the blackjack STAND (R2696). The
    // casino group prints the claim; this row names the order act and
    // claims nothing, so the claim check counts the key once.
    { key: KEYS.map.casino_stand.label, claims: [], does: S.does_next_round },
    row('scan', S.does_scan),
    row('gather', S.does_gather),
    row('move', S.does_move),
    row('lane', S.does_lane),
    row('hold_fire', S.does_hold_fire),
    // Enter and `d` press DONE of the plot line while it stands (R3185).
    // The menus and the base group print the claims, so these rows claim
    // nothing, as the round switch row does.
    { key: KEYS.map.menu_focus.label, claims: [], does: S.does_plot_done },
    { key: KEYS.map.base_tubes.label, claims: [], does: S.does_plot_done },
  ],
  // The fleet control groups (R539). The claims are written from the slot
  // count the server and the client both read, so the reference cannot
  // state a slot the model refuses.
  groups: [
    row('group_recall', S.does_group_recall),
    // Two chords for one action (R543): a browser that keeps `ctrl-digit`
    // for its own tabs still gives the page the shift one.
    row('group_assign', S.does_group_assign),
  ],
  // One row a key (R543). The KEYS panel gives the key column a fixed
  // 92 px and does not wrap it, so a label of two chords does not fit.
  menus: [
    row('menu_next', S.does_menu_next),
    row('menu_prev', S.does_menu_prev),
    // Tab walks the menus from the map alone, and Enter puts the focus
    // into the open menu, where Tab walks the controls (R2052).
    row('menu_focus', S.does_menu_focus),
    // The arrow keys walk the rows of the open menu (R2083).
    row('row_step', S.does_row_step),
    row('row_ends', S.does_row_ends),
    // The pills of the open menu, on the same key (R543). Every browser
    // keeps this chord for its own tabs in a normal window, so the
    // shift-alt row below is the fallback and stays.
    row('section_next', S.does_section_next),
    row('section_prev', S.does_section_prev),
    row('sections', S.does_sections),
    row('music', S.does_music),
    // The INBOX, the act of its tab (R3100).
    row('inbox', S.does_inbox),
  ],
  base: [
    row('base_tubes', S.does_base_tubes),
    // `n` steps the hold's round pick too (R2783). The casino group
    // prints the claim, so this row claims nothing, as the orders row does.
    { key: KEYS.map.casino_stand.label, claims: [], does: S.does_hold_round },
  ],
  // The casino's table keys (casino.md §9, R853, R2143). Ten caps that are
  // free on the map surface, one row each. `Overlay.onKey` forwards the
  // group to `CasinoPanel.key` while the CASINO tab stands open, and the
  // panel hands a press to the screen on the display alone.
  casino: [
    row('casino_hit', S.does_casino_hit),
    row('casino_stand', S.does_casino_stand),
    row('casino_double', S.does_casino_double),
    row('casino_fold', S.does_casino_fold),
    row('casino_call', S.does_casino_call),
    row('casino_bet', S.does_casino_bet),
    row('casino_spin', S.does_casino_spin),
    row('casino_rebet', S.does_casino_rebet),
    row('casino_take_back', S.does_casino_take_back),
    row('casino_clear', S.does_casino_clear),
  ],
  debug: [row('kraken', S.does_kraken), row('admin', S.does_admin)],
  // The hidden view toggles (R456, ui-map-interface.md §9). Every row is
  // `hidden`, so the KEYS panel drops the whole group. The first three
  // flip an OPTIONS row and persist with it; the last five are the
  // session-only layer toggles of `ui/layerview.ts`.
  layers: [
    row('fluff', S.does_fluff),
    row('sonar_visuals', S.does_sonar_visuals),
    row('screenshake', S.does_screenshake),
    ...LAYER_TOGGLES.map((t) => row(t.id, t.does)),
  ],
};

/** The heading of each group of the card keys. */
const CARD_GROUP_NAMES: Record<typeof KEY_GROUPS.card[number], string> = {
  docked: S.group_docked,
  helm: S.group_helm,
};

/** One reference row of the card keys (R1989). */
function cardRow(id: CardKeyId, does: string): KeyBinding {
  return bindingRow(KEYS.card[id], does);
}

/** The rows of the card keys: DEPLOY and REPAIR of the deploy ask. */
const CARD_ROWS: Record<typeof KEY_GROUPS.card[number], KeyBinding[]> = {
  docked: [cardRow('deploy', S.does_card_deploy), cardRow('repair', S.does_card_repair)],
  // The helm keys of the map (R2707): they win over `row_step` while one
  // own fleet at sea is selected.
  helm: [
    cardRow('steer_port', S.does_card_steer_port), cardRow('steer_starboard', S.does_card_steer_starboard),
    cardRow('telegraph_up', S.does_card_telegraph_up), cardRow('telegraph_down', S.does_card_telegraph_down),
  ],
};

/** Every group of the season map, in the order the pack states (ORDERS
 *  first, R2184), then the keys of the card of a docked fleet. The heat
 *  maps fold. */
export const KEYMAP: KeyGroup[] = [
  ...KEY_GROUPS.map.map((g) => ({
    name: GROUP_NAMES[g], keys: GROUP_ROWS[g], fold: g === 'view', ...(g === 'casino' ? { casino: true } : {}),
  })),
  ...KEY_GROUPS.card.map((g) => ({ name: CARD_GROUP_NAMES[g], keys: CARD_ROWS[g], over: true })),
];

/** The groups a key reference shows: every group, less the casino keys
 *  in a store app (mobile-apps.md §8, R2927). */
export function shownKeymap(noCasino: boolean = casinoHidden()): KeyGroup[] {
  return noCasino ? KEYMAP.filter((g) => !g.casino) : KEYMAP;
}

/** Every claimed key that appears more than once — must be empty. A
 *  group of card keys (`over`) is left out: it wins over the map claim
 *  while its card stands (R1989). */
export function keymapConflicts(groups: KeyGroup[] = KEYMAP): string[] {
  const seen = new Set<string>();
  const dup = new Set<string>();
  for (const g of groups) {
    if (g.over) continue;
    for (const k of g.keys) {
      for (const c of k.claims) (seen.has(c) ? dup : seen).add(c);
    }
  }
  return [...dup].sort();
}
